import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigationManager } from '@provider/navigation';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { getAllRevisionsQueryKey } from './query.get-all.js';
import { activateRevisionRequest } from './request.activate.js';

function useActivateRevisionMutation(courseId) {
  const client = useQueryClient();
  const notifier = useNotifier();
  const navigationManager = useNavigationManager();
  const user = useUserIdentityState(state => state.user);
  return useMutation(activateRevisionMutation(client, navigationManager, notifier, user, courseId));
}
const activateRevisionMutation = (client, navigationManager, notifier, user, courseId) => {
  if (courseId === undefined) {
    throw new Error("activateRevisionMutation(): called without providing a correct courseId");
  }
  return {
    mutationKey: ["course-revision/activate", user?.id],
    mutationFn: async revision => await activateRevisionRequest(user, revision),
    onMutate: async input => {
      const prev = client.getQueryData(getAllRevisionsQueryKey(user, courseId))?.find(it => it.isActive);
      client.setQueryData(getAllRevisionsQueryKey(user, courseId), prevRevisions => prevRevisions?.map(revision => {
        if (revision.__clientId === input.__clientId) {
          return {
            ...revision,
            isActive: true
          };
        }
        if (revision.__clientId === prev?.__clientId) {
          return {
            ...revision,
            isActive: false
          };
        }
        return revision;
      }));

      // push the newly activated revisionId to the current URL, as to allow
      // the user to copy + paste it and share it that way
      const currLocation = navigationManager.getLocation();
      currLocation.searchParams.set("revisionId", input.id);
      currLocation.searchParams.set("revisionTitle", input.name);
      navigationManager.replace(`${currLocation.pathname}?${currLocation.searchParams.toString()}#${currLocation.hash.replace("#", "")}`);
      await client.cancelQueries({
        queryKey: getAllRevisionsQueryKey(user, courseId)
      });
      return {
        prev
      };
    }
  };
};

export { useActivateRevisionMutation };
//# sourceMappingURL=mutation.activate.js.map
